/**
 * Emmergency component
 * Displays an emergency banner and modal with details, including published date and description.
 * Handles modal open/close logic and mobile/desktop title rendering.
 */

"use client";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";
import { Modal } from "@/components/atoms/modal";

import { formatDateWithLocale } from "@/constants/date";
import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import { useModal } from "@/hooks/useModal/useModal";
import { Clock5 } from "lucide-react";

export interface IEmmergency {
  title?: null | string;
  description?: null | string;
  titleMobile?: null | string;
  publishedAt?: string | null;
  locale: string;
  isCustomTemplate?: boolean;
}

export default function Emmergency({
  title,
  description,
  titleMobile,
  publishedAt,
  locale,
  isCustomTemplate
}: Readonly<IEmmergency>) {
  const { isOpen, toggleModal } = useModal();
  const isMobile = useIsMobile();

  return (
    <>
      <button
        type="button"
        onClick={toggleModal}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls="emergency-modal"
        className={`flex h-[var(--offer-height)] w-full cursor-pointer items-center justify-center bg-[var(--fill-red-solid-normal)] px-5 py-3 text-xs leading-4 font-medium text-[var(--text-gray-base)] max-md:h-[var(--mobile-offer-height)] ${isCustomTemplate ? "fixed top-0 z-50" : ""}`}
      >
        {isMobile ? titleMobile : title}
      </button>
      <Modal
        open={isOpen}
        toggleModal={toggleModal}
        containerClassName="max-w-sm md:max-w-2xl"
        footerClassName="hidden"
        closeClassName="right-2 top-2 bg-[var(--fill-base-main)] text-white rounded-full opacity-100 p-1.5"
        header={null}
      >
        <div
          id="emergency-modal"
          className="p-6 pt-8 text-sm text-[var(--text-base-primary)]"
        >
          <RichtextRenderer content={description || ""} />

          {publishedAt && (
            <div className="mt-6 flex items-center justify-end gap-1 text-sm text-[var(--text-base-primary)]">
              <Clock5 className="h-4 w-4" />
              <span>
                投稿日 {formatDateWithLocale(new Date(publishedAt), locale)}
              </span>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
